/**
 * Step Function managed lambdas: Datadog APM is always enabled in the step-function Terraform module.
 * Strip from config so it is not emitted or edited in the managed-lambdas UI.
 */
export function omitDatadogLambdaApmFromLambdas<T extends Record<string, any>>(
  lambdas: Record<string, T>,
): Record<string, Omit<T, 'datadog_lambda_apm_enabled'>> {
  const out = {} as Record<string, Omit<T, 'datadog_lambda_apm_enabled'>>;
  for (const [name, lambda] of Object.entries(lambdas)) {
    if (!lambda || typeof lambda !== 'object' || Array.isArray(lambda)) {
      // Preserve malformed entries as-is (runtime JSON may not match T).
      out[name] = lambda as Omit<T, 'datadog_lambda_apm_enabled'>;
      continue;
    }
    const { datadog_lambda_apm_enabled: _omit, ...rest } = lambda as T & {
      datadog_lambda_apm_enabled?: boolean;
    };
    out[name] = rest as Omit<T, 'datadog_lambda_apm_enabled'>;
  }
  return out;
}

/**
 * LMI (Lambda Managed Instances) lambdas — those with a `capacity_provider` set — do not
 * support SnapStart, the Parameters & Secrets extension layer, or VPC settings (security
 * groups). Strip those fields so incompatible config is never emitted, and always set
 * `snap_alias: true` since LMI lambdas require a snap alias.
 */
export function stripLmiIncompatibleFieldsFromLambdas<T extends Record<string, any>>(
  lambdas: Record<string, T>,
): Record<string, T> {
  const out = {} as Record<string, T>;
  for (const [name, lambda] of Object.entries(lambdas)) {
    if (!lambda || typeof lambda !== 'object' || Array.isArray(lambda)) {
      out[name] = lambda;
      continue;
    }
    const cp = (lambda as { capacity_provider?: unknown }).capacity_provider;
    if (typeof cp === 'string' && cp !== '') {
      const {
        snapstart: _snap,
        param_and_secrets_ext_enabled: _secrets,
        sg_ids: _sg,
        ...rest
      } = lambda as T & {
        snapstart?: unknown;
        param_and_secrets_ext_enabled?: unknown;
        sg_ids?: unknown;
      };
      // LMI lambdas always create a snap alias.
      out[name] = { ...rest, snap_alias: true } as unknown as T;
    } else {
      out[name] = lambda;
    }
  }
  return out;
}
